package com.example.positionahead.data

import com.example.positionahead.model.PriceAlert
import com.russhwolf.settings.Settings
import com.russhwolf.settings.set
import kotlin.random.Random
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json

class AlertRepository(
    private val settings: Settings,
    private val json: Json = Json { ignoreUnknownKeys = true },
) {
    private var cache: List<PriceAlert>? = null

    fun getAlerts(): List<PriceAlert> {
        cache?.let { return it }
        val raw = settings.getStringOrNull(KEY_ALERTS) ?: return emptyList<PriceAlert>().also { cache = it }
        cache = runCatching { json.decodeFromString<List<PriceAlert>>(raw) }.getOrDefault(emptyList())
        return cache!!
    }

    fun saveAlerts(alerts: List<PriceAlert>) {
        cache = alerts
        settings[KEY_ALERTS] = json.encodeToString(alerts)
    }

    fun addAlert(alert: PriceAlert) {
        saveAlerts(getAlerts() + alert)
    }

    fun createAlert(
        symbol: String,
        type: com.example.positionahead.model.AlertType,
        threshold: Double,
        note: String = "",
    ): PriceAlert {
        val alert = PriceAlert(
            id = Random.nextLong().toString(16),
            symbol = symbol.uppercase(),
            type = type,
            threshold = threshold,
            note = note,
        )
        addAlert(alert)
        return alert
    }

    fun updateAlert(alert: PriceAlert) {
        saveAlerts(getAlerts().map { if (it.id == alert.id) alert else it })
    }

    fun deleteAlert(id: String) {
        saveAlerts(getAlerts().filterNot { it.id == id })
    }

    fun markTriggeredRead(ids: List<String>) {
        saveAlerts(
            getAlerts().map { alert ->
                if (alert.id in ids) alert.copy(read = true) else alert
            },
        )
    }

    fun clearTriggered(id: String) {
        saveAlerts(
            getAlerts().map { alert ->
                if (alert.id == id) alert.copy(triggeredAt = null, read = false) else alert
            },
        )
    }

    fun getUnreadTriggered(): List<PriceAlert> =
        getAlerts().filter { it.triggeredAt != null && !it.read }

    companion object {
        private const val KEY_ALERTS = "price_alerts_json"
    }
}
